import { CandidateProfile, FocusArea } from '../services/cvGenerator';
import { useLanguage } from '../i18n/LanguageContext';
import { useRef, useEffect } from 'react';
import azureLogo from '../img/logos/Microsoft_Azure_Logo.svg';
import sharePointLogo from '../img/logos/Microsoft_Office_SharePoint.svg';
import microsoft365Logo from '../img/logos/Microsoft_365_(2022).svg';
import dotNetLogo from '../img/logos/Microsoft_.NET_logo.svg';
import powerPlatformLogo from '../img/logos/PowerPlatform_scalable.svg';
import reactLogo from '../img/logos/React_Logo_SVG.svg';
import copilotLogo from '../img/logos/Microsoft_copilot.svg';

interface HomePageProps {
  candidateProfile: CandidateProfile | null;
  availableFocusAreas: FocusArea[];
}

const TECHNOLOGY_LOGOS = [
  {
    name: 'Azure',
    description: 'Microsoft Azure cloud platform',
    iconUrl: azureLogo,
  },
  {
    name: 'SharePoint',
    description: 'Microsoft SharePoint platform',
    iconUrl: sharePointLogo,
  },
  {
    name: 'Microsoft 365',
    description: 'Microsoft 365 suite of productivity tools',
    iconUrl: microsoft365Logo,
  },
  {
    name: '.NET',
    description: 'Microsoft .NET framework and runtime',
    iconUrl: dotNetLogo,
  },
  {
    name: 'Power Platform',
    description: 'Microsoft Power Platform for business apps',
    iconUrl: powerPlatformLogo,
  },
  {
    name: 'React',
    description: 'React JavaScript library for user interfaces',
    iconUrl: reactLogo,
  },
  {
    name: 'Copilot',
    description: 'Microsoft Copilot',
    iconUrl: copilotLogo,
  },
];

const HomePage = ({ candidateProfile, availableFocusAreas }: HomePageProps) => {
  const { t } = useLanguage();
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    let autoScrollAnimationId: number | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    
    // Store handler references for cleanup
    let handleScroll: (() => void) | null = null;
    let handleMouseDown: ((e: MouseEvent) => void) | null = null;
    let handleMouseMove: ((e: MouseEvent) => void) | null = null;
    let handleMouseUp: (() => void) | null = null;
    let handleMouseLeave: (() => void) | null = null;

    const stopAutoScroll = (): void => {
      if (autoScrollAnimationId !== null) {
        cancelAnimationFrame(autoScrollAnimationId);
        autoScrollAnimationId = null;
      }
    };

    const startAutoScroll = (): void => {
      if (autoScrollAnimationId !== null) {
        cancelAnimationFrame(autoScrollAnimationId);
      }
      const autoScroll = (): void => {
        carousel.scrollLeft += 1; // scrollSpeed = 1
        autoScrollAnimationId = requestAnimationFrame(autoScroll);
      };
      autoScrollAnimationId = requestAnimationFrame(autoScroll);
    };

    // Wait for layout to settle before calculating dimensions
    const measureAndStart = (): void => {
      // Measure the actual scrollable width of one set of items
      const scrollWidth = carousel.scrollWidth;
      const oneSetWidth = scrollWidth / 2; // We duplicated items, so divide by 2

      handleScroll = (): void => {
        // Reset to start when reaching the end of first set (with 5px tolerance for rounding)
        if (carousel.scrollLeft >= oneSetWidth + 5) {
          carousel.scrollLeft = 0;
        }
      };

      handleMouseDown = (e: MouseEvent): void => {
        isDragging = true;
        startX = e.pageX - carousel.offsetLeft;
        scrollLeft = carousel.scrollLeft;
        stopAutoScroll();
        carousel.style.cursor = 'grabbing';
      };

      handleMouseMove = (e: MouseEvent): void => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - carousel.offsetLeft;
        const walk = (x - startX) * 2;
        carousel.scrollLeft = scrollLeft - walk;
      };

      handleMouseUp = (): void => {
        isDragging = false;
        carousel.style.cursor = 'grab';
        startAutoScroll();
      };

      handleMouseLeave = (): void => {
        if (isDragging) {
          isDragging = false;
          carousel.style.cursor = 'grab';
          startAutoScroll();
        }
      };

      carousel.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      carousel.addEventListener('mouseleave', handleMouseLeave);
      carousel.addEventListener('scroll', handleScroll);

      startAutoScroll();
    };

    // Measure after a brief delay to ensure layout is settled
    timeoutId = setTimeout(measureAndStart, 100);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      stopAutoScroll();
      if (handleMouseDown) carousel.removeEventListener('mousedown', handleMouseDown);
      if (handleMouseMove) document.removeEventListener('mousemove', handleMouseMove);
      if (handleMouseUp) document.removeEventListener('mouseup', handleMouseUp);
      if (handleMouseLeave) carousel.removeEventListener('mouseleave', handleMouseLeave);
      if (handleScroll) carousel.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <section className="panel home-hero">
        <h1>{t.home.companyName}</h1>
        <p className="profile-subtitle">
          {t.home.companyTagline}
        </p>
        <p className="profile-bio">
          {t.home.companyBio}
        </p>
      </section>

      <section className="panel">
        <div className="tech-stack">
          <div className="tech-logo-grid" ref={carouselRef}>
            {[...TECHNOLOGY_LOGOS, ...TECHNOLOGY_LOGOS].map((technology, index) => (
              <article key={`${technology.name}-${index}`} className="tech-logo-card" title={technology.description}>
                <img
                  className="tech-logo"
                  src={technology.iconUrl}
                  alt={`${technology.name} logo`}
                  loading="lazy"
                />
                <span>{technology.name}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="panel">
        <h2>{t.home.focusAreas}</h2>
        <div className="skills-grid">
          {availableFocusAreas.map((area) => (
            <div key={area} className="skill-badge">{area}</div>
          ))}
        </div>
      </section>

      <section className="panel">
        <h2>{t.home.serviceHighlights}</h2>
        <div className="service-grid">
          <article className="service-card">
            <h3>{t.home.architecture}</h3>
            <p>
              {t.home.architectureDesc}
            </p>
          </article>
          <article className="service-card">
            <h3>{t.home.powerPlatform}</h3>
            <p>
              {t.home.powerPlatformDesc}
            </p>
          </article>
          <article className="service-card">
            <h3>{t.home.development}</h3>
            <p>
              {t.home.developmentDesc}
            </p>
          </article>
          <article className="service-card">
            <h3>{t.home.platform}</h3>
            <p>
              {t.home.platformDesc}
            </p>
          </article>
        </div>
      </section>
    </>
  );
};

export default HomePage;
